// src/components/DetailUmat.js
import { resolveUmatAvatarSync, getUmatDisplayLabel, getUmatDisplayLabelLine2, getUmatAreaPinyin } from "../utils/wkCommon";
import React, { useState, useEffect } from "react";
import CardUmatActions from "./CardUmatActions";
import { isZh } from "../utils/language";

/**
 * normalizeAndExtractPhones(raw)
 * - menerima string (bisa berisi catatan, koma, slash, teks)
 * - mengembalikan array nomor yang sudah dinormalisasi ke bentuk +62... (unique, urutan sama)
 */
function normalizeAndExtractPhones(raw) {
  if (!raw && raw !== 0) return [];

  const segments = String(raw).split("/").map(s => s.trim()).filter(Boolean);
  if (segments.length === 0) return [];

  const results = [];

  // helper normalize ke +62
  function normalize(t) {
    t = t.replace(/[^\d+]/g, "");
    t = t.replace(/^\++/, "+");

    t = t[0] === "+"
      ? "+" + t.slice(1).replace(/\D/g, "")
      : t.replace(/\D/g, "");

    const digitsOnly = t.replace(/\D/g, "");
    if (digitsOnly.length < 6) return null;

    if (t.startsWith("+")) {
      if (t.startsWith("+62")) return t;
      return null;
    } else if (t.startsWith("0")) {
      return "+62" + t.slice(1);
    } else if (t.startsWith("62")) {
      return "+" + t;
    } else if (t.startsWith("8")) {
      return "+62" + t;
    }

    return null;
  }

  // 🔹 1. proses base number
  const baseRaw = segments[0];
  const baseNormalized = normalize(baseRaw);

  if (!baseNormalized) return [];

  results.push(baseNormalized);

  const baseDigits = baseNormalized.replace(/^\+/, "");

  // 🔹 2. proses segmen lain
  for (let i = 1; i < segments.length; i++) {
    let seg = segments[i];

    // bersihkan
    let clean = seg.replace(/\D/g, "");

    if (!clean) continue;

    // 🔥 jika pendek → treat sebagai suffix
    if (clean.length <= 4) {
      const newDigits =
        baseDigits.slice(0, baseDigits.length - clean.length) + clean;

      const newNumber = "+" + newDigits;

      if (!results.includes(newNumber)) {
        results.push(newNumber);
      }
    } else {
      // normal number
      const n = normalize(seg);
      if (n && !results.includes(n)) {
        results.push(n);
      }
    }
  }

  return results;
}

function prettyPhoneDisplay(phone) {
  if (!phone) return "";
  const digits = phone.replace(/^\+/, ""); 
  if (digits.startsWith("62")) { 
    let rest = digits.slice(2); 
    //first part slice(3) -- +62 812 xxxx xxxx 
    let firstPart = rest.slice(0,3); 
    rest = rest.slice(3) 
    const parts = []; 
    parts.push(firstPart) 
    for (let i = 0; i < rest.length; i += 4) 
      parts.push(rest.slice(i, i + 4)); 
    return "+" + "62 " + parts.join(" "); 
  }
  return phone; 
}

function computeAge(birthdate) {
  if (!birthdate) return null;
  const b = new Date(birthdate);
  if (isNaN(b.getTime())) return null;
  const now = new Date();
  const diff = now - b;
  const age = Math.floor(diff / (365.25 * 24 * 60 * 60 * 1000));
  return age;
}

/**
 * getParentFromRelationOrFields(umat, relationFieldName, nameFieldFallback, phoneFieldFallback)
 * - prioritas: if relation (object) present, try to read name & phone from that relation
 * - fallback: use umat[nameFieldFallback] & umat[phoneFieldFallback]
 * - returns { name: string|null, phone: string|null }
 */
function getParentFromRelationOrFields(umat, relationFieldName, nameFieldFallback, phoneFieldFallback) {
  // check relation object in raw (populated by API?).
  const raw = umat.raw || {};
  let rel = raw[relationFieldName];

  // relation could be { id, attributes } or flattened object
  if (rel) {
    if (rel.attributes) {
      // Strapi v4-like populate shape
      const attrs = rel.attributes || {};
      return {
        name: attrs.chineseName || attrs.fullname || attrs.nickName || null,
        phone: attrs.mobilePhone || null,
      };
    } else if (typeof rel === "object") {
      // maybe already normalized object
      return {
        name: rel.chineseName || rel.fullname || rel.nickName || null,
        phone: rel.mobilePhone || null,
      };
    }
  }
  // fallback to direct fields on umat
  return {
    name: raw[nameFieldFallback] || null,
    phone: raw[phoneFieldFallback] || null,
  };
}

export default function DetailUmat({ umat, onClose, canViewUmatAge }) {
  const [showPrivateInfo, setShowPrivateInfo] = useState(false);

  if (!umat) return null;

  const avatar = resolveUmatAvatarSync(umat.raw || umat);
  const age = computeAge(umat.birthdate || umat.tanggalLahir || null);
  const hideSelfMobile = (age !== null) ? (age <= 12) : false;
  const showParents = (age !== null) ? (age <= 15) : false;

  // get parent's info (prioritize relation fields)
  const fatherInfo = getParentFromRelationOrFields(umat, "father_umat_item", "fatherName", "fatherPhoneNo");
  const motherInfo = getParentFromRelationOrFields(umat, "mother_umat_item", "motherName", "motherPhoneNo");

  // normalisasi nomor sesuai prior implementation
  const selfPhones = normalizeAndExtractPhones(umat.mobilePhone || umat.mobile || umat.phone || "");
  const fatherPhones = normalizeAndExtractPhones(fatherInfo.phone || "");
  const motherPhones = normalizeAndExtractPhones(motherInfo.phone || "");

  const hasAge = age !== null;
  const hasAddress = !!(umat.address && umat.address.trim() !== "");
  const canShowPrivateToggle = canViewUmatAge && (hasAge || hasAddress);

  const hasChinese = umat.chineseName || umat.pinyinName;
  const displayName = umat.fullName || umat.nickName || "-";

  const nameLine2 = getUmatDisplayLabelLine2(umat);
  const showLine2 = nameLine2 != "—" && nameLine2 != "";
  return (
    <div className="detail-card" style={{ fontSize: "1rem" }}>
      <div className="detail-head" style={{ display: "flex", justifyContent: "space-between", gap: "0.75rem", alignItems: "center" }}>
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
          <img src={avatar} alt={umat.fullName || umat.nickName || ""} style={{ width: "156px", height: "156px", borderRadius: 8, objectFit: "cover" }} />
        </div>

        <button className="btn-icon close-detail" onClick={onClose} aria-label="Close detail" style={{ fontSize: "1.1rem" }}>✕</button>
      </div>

      <div className="detail-body" style={{ marginTop: "0.75rem", lineHeight: 1.4 }}>
        <div style={{ fontSize: "1.125rem", fontWeight: 700 }}>
          {getUmatDisplayLabel(umat)}
        </div>

        {showLine2 && 
          <div style={{ opacity: 0.8 }}>
            {nameLine2}
          </div>
        }
      </div>
      <div className="detail-body" style={{ marginTop: "0.75rem", lineHeight: 1.4 }}>
        <div style={{ display: "flex", gap: "0.75rem", marginBottom: "0.5rem" }}>
          <div style={{ flex: 1 }}>
            <div className="detail-row"><strong>{isZh() ? "性別：" : "Gender:"}</strong> {umat.gender || "-"}</div>
            <div className="detail-row"><strong>{isZh() ? "區域：" : "Area:"}</strong> { getUmatAreaPinyin(umat) }</div>
            {canShowPrivateToggle  && (
              <div style={{ marginTop: "0.5rem" }}>
                <div
                  style={{
                    cursor: "pointer",
                    fontWeight: 600,
                    color: "#0b63a8"
                  }}
                  onClick={() => setShowPrivateInfo(prev => !prev)}
                >
                  {showPrivateInfo 
                    ? (isZh() 
                      ? "▼ 隱藏個人資訊" 
                      : "▼ Tutup Informasi Pribadi")
                    : (isZh() 
                      ? "▶ 查看年齡與地址" 
                      : "▶ Lihat Usia & Alamat")}
                </div>

                {showPrivateInfo && (
                  <div style={{ marginTop: "0.5rem", paddingLeft: "0.5rem" }}>
                    <div className="detail-row">
                      <strong>{isZh() ? "年齡：" : "Usia:"}</strong> {age === null ? "-" : (isZh() ? `${age} 歲` : `${age} tahun`)}
                    </div>

                    <div className="detail-row">
                      <strong>{isZh() ? "地址：" : "Alamat:"}</strong> {umat.address || "-"}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Mobile: hide if age <= 12 */}
        <div style={{ display: "flex", gap: "0.75rem", marginBottom: "0.5rem", alignItems: "flex-start" }}>
          <div style={{ flex: 1 }}>
              {!hideSelfMobile && (
                <>
                <div className="detail-row">
                  <strong>{isZh() ? "手機：" : "Mobile:"}</strong>{" "}
                  {selfPhones.length === 0 ? <span>-</span> : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem", marginTop: "0.25rem" }}>
                      {selfPhones.map((p) => {
                        const waNumber = p.replace(/^\+/, "");
                        const waHref = `https://wa.me/${waNumber}`;
                        return (
                          <div key={p} style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}>
                            <span style={{ fontVariantNumeric: "tabular-nums" }}>{prettyPhoneDisplay(p)}</span>
                            <a href={waHref} target="_blank" rel="noopener noreferrer" title={`Chat ${prettyPhoneDisplay(p)} via WhatsApp`} aria-label={`Chat ${p} via WhatsApp`} style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                                <path d="M20.52 3.48A11.94 11.94 0 0012 0C5.37 0 .02 5.35.02 12a11.9 11.9 0 001.95 6.54L0 24l5.68-1.84A11.96 11.96 0 0012 24c6.63 0 12-5.35 12-12 0-3.2-1.25-6.2-3.48-8.52z" fill="#25D366"/>
                                <path d="M17.47 14.24c-.3-.15-1.78-.88-2.05-.98-.27-.1-.46-.15-.65.15s-.75.98-.92 1.18c-.17.2-.33.22-.62.07-.3-.15-1.27-.47-2.42-1.49-.9-.8-1.51-1.79-1.69-2.09-.17-.3-.02-.46.13-.61.13-.13.3-.33.45-.5.15-.17.2-.28.3-.47.1-.18.05-.34-.02-.48-.07-.14-.65-1.57-.89-2.15-.23-.57-.47-.5-.65-.51l-.55-.01c-.18 0-.47.07-.72.34-.25.27-.97.95-.97 2.32 0 1.37 1 2.7 1.15 2.89.15.2 1.99 3.13 4.82 4.26 3.03 1.23 3.03.82 3.58.78.55-.04 1.78-.72 2.03-1.42.25-.7.25-1.3.18-1.42-.07-.12-.27-.19-.57-.34z" fill="#fff"/>
                              </svg>
                            </a>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="detail-row" style={{ marginTop: "0.5rem" }}><strong>{isZh() ? "電子郵件：" : "Email:"}</strong> {umat.email || "-"}</div>
                </>
              )}
          </div>
        </div>

        {/* Parents: show if age <= 15 */}
        {showParents && (
          (!fatherInfo.name && fatherPhones.length === 0 && !motherInfo.name && motherPhones.length === 0) ? (
            <div>{isZh() ? "沒有父母資料。" : "Tidak ada data orangtua."}</div>
          ) : (
          <div style={{ marginTop: "0.5rem", borderTop: "1px dashed rgba(0,0,0,0.06)", paddingTop: "0.5rem" }}>
            <div style={{ fontSize: "0.95rem", fontWeight: 700, marginBottom: "0.4rem" }}>{ isZh() ? "父母聯絡資訊" : "Kontak Orangtua"}</div>

            <div style={{ display: "flex", gap: "0.75rem", marginBottom: "0.5rem", alignItems: "flex-start" }}>
              <div style={{  }}>
                <div className="detail-row">
                  {fatherInfo.name}
                </div>
                <div className="detail-row" style={{ marginLeft: "0.5rem" }}>
                  {" "}
                  {fatherPhones.length === 0 ? <span></span> : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem", marginTop: "0.25rem" }}>
                      {fatherPhones.map(p => {
                        const waNumber = p.replace(/^\+/, "");
                        const waHref = `https://wa.me/${waNumber}`;
                        return (
                          <div key={p} style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}>
                            <span style={{ fontVariantNumeric: "tabular-nums" }}>{prettyPhoneDisplay(p)}</span>
                            <a href={waHref} target="_blank" rel="noopener noreferrer" title={`Chat ${prettyPhoneDisplay(p)} via WhatsApp`} aria-label={`Chat ${p} via WhatsApp`} style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                                <path d="M20.52 3.48A11.94 11.94 0 0012 0C5.37 0 .02 5.35.02 12a11.9 11.9 0 001.95 6.54L0 24l5.68-1.84A11.96 11.96 0 0012 24c6.63 0 12-5.35 12-12 0-3.2-1.25-6.2-3.48-8.52z" fill="#25D366"/>
                                <path d="M17.47 14.24c-.3-.15-1.78-.88-2.05-.98-.27-.1-.46-.15-.65.15s-.75.98-.92 1.18c-.17.2-.33.22-.62.07-.3-.15-1.27-.47-2.42-1.49-.9-.8-1.51-1.79-1.69-2.09-.17-.3-.02-.46.13-.61.13-.13.3-.33.45-.5.15-.17.2-.28.3-.47.1-.18.05-.34-.02-.48-.07-.14-.65-1.57-.89-2.15-.23-.57-.47-.5-.65-.51l-.55-.01c-.18 0-.47.07-.72.34-.25.27-.97.95-.97 2.32 0 1.37 1 2.7 1.15 2.89.15.2 1.99 3.13 4.82 4.26 3.03 1.23 3.03.82 3.58.78.55-.04 1.78-.72 2.03-1.42.25-.7.25-1.3.18-1.42-.07-.12-.27-.19-.57-.34z" fill="#fff"/>
                              </svg>
                            </a>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="detail-row">
                    {motherInfo.name}
                </div>
                <div className="detail-row" style={{ marginLeft: "0.5rem" }}>
                  {" "}
                  {motherPhones.length === 0 ? <span></span> : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem", marginTop: "0.25rem" }}>
                      {motherPhones.map(p => {
                        const waNumber = p.replace(/^\+/, "");
                        const waHref = `https://wa.me/${waNumber}`;
                        return (
                          <div key={p} style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}>
                            <span style={{ fontVariantNumeric: "tabular-nums" }}>{prettyPhoneDisplay(p)}</span>
                            <a href={waHref} target="_blank" rel="noopener noreferrer" title={`Chat ${prettyPhoneDisplay(p)} via WhatsApp`} aria-label={`Chat ${p} via WhatsApp`} style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                                <path d="M20.52 3.48A11.94 11.94 0 0012 0C5.37 0 .02 5.35.02 12a11.9 11.9 0 001.95 6.54L0 24l5.68-1.84A11.96 11.96 0 0012 24c6.63 0 12-5.35 12-12 0-3.2-1.25-6.2-3.48-8.52z" fill="#25D366"/>
                                <path d="M17.47 14.24c-.3-.15-1.78-.88-2.05-.98-.27-.1-.46-.15-.65.15s-.75.98-.92 1.18c-.17.2-.33.22-.62.07-.3-.15-1.27-.47-2.42-1.49-.9-.8-1.51-1.79-1.69-2.09-.17-.3-.02-.46.13-.61.13-.13.3-.33.45-.5.15-.17.2-.28.3-.47.1-.18.05-.34-.02-.48-.07-.14-.65-1.57-.89-2.15-.23-.57-.47-.5-.65-.51l-.55-.01c-.18 0-.47.07-.72.34-.25.27-.97.95-.97 2.32 0 1.37 1 2.7 1.15 2.89.15.2 1.99 3.13 4.82 4.26 3.03 1.23 3.03.82 3.58.78.55-.04 1.78-.72 2.03-1.42.25-.7.25-1.3.18-1.42-.07-.12-.27-.19-.57-.34z" fill="#fff"/>
                              </svg>
                            </a>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>)
        )}

        {/* QR dari kode umat */}
        <div style={{ marginTop: "0.75rem", textAlign: "center" }}>
          <div style={{ fontSize: "0.85rem", color: "#666", marginBottom: "0.5rem" }}>{isZh() ? "道親編號 QR 碼" : "QR dari Kode Umat"}</div>
          <img src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(umat.umatID || umat.id || umat.email || "")}`} alt={`QR-${umat.umatID || umat.id}`} style={{ width: "10rem", height: "10rem", display: "inline-block" }} />
          <div style={{ fontSize: "1rem", marginTop: "0.5rem" }}><strong>{umat.umatID}</strong></div>
        </div>

        <CardUmatActions umat={umat} />        
      </div>
    </div>
  );
}
